import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { Field } from "@/components/editor/Field";
import { DriveWarning } from "@/components/editor/DriveWarning";
import { CoverImageField } from "@/components/editor/CoverImageField";
import { ContentField } from "@/components/editor/ContentField";
import { VideoField } from "@/components/editor/VideoField";
import { TagsField } from "@/components/editor/TagsField";

const empty = {
    title: "", category: "news", excerpt: "", content: "",
    cover_image: "", video_url: "", author_name: "", published: true, tags: [],
};

export default function PostEditor() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [fetching, setFetching] = useState(isEdit);
    const [driveConnected, setDriveConnected] = useState(true); // optimistic

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/drive/status");
                setDriveConnected(Boolean(data.connected));
            } catch {
                setDriveConnected(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (loading) return;
        if (!user?.is_admin) { navigate("/login", { replace: true }); return; }
        if (isEdit) {
            (async () => {
                try {
                    const { data } = await api.get("/admin/posts");
                    const p = data.find(x => x.id === id);
                    if (p) setForm({
                        title: p.title, category: p.category, excerpt: p.excerpt,
                        content: p.content, cover_image: p.cover_image || "",
                        video_url: p.video_url || "",
                        author_name: p.author_name || "",
                        published: p.published,
                        tags: p.tags || [],
                    });
                } catch { toast.error("Failed to load post"); }
                finally { setFetching(false); }
            })();
        }
    }, [user, loading, id, isEdit, navigate]);

    const handleChange = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.excerpt.trim() || !form.content.trim()) {
            toast.error("Title, excerpt, and content are required");
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await api.put(`/posts/${id}`, form);
                toast.success("Post updated");
            } else {
                await api.post("/posts", form);
                toast.success("Post created");
            }
            navigate("/admin");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    if (loading || fetching) {
        return <div className="min-h-screen grid place-items-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Loading...</div>;
    }

    return (
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-12" data-testid="post-editor">
            <Toaster position="top-right" theme="dark" />
            <Link to="/admin" className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 hover:text-[#FFD000] inline-flex items-center gap-2 mb-6" data-testid="editor-back">
                <ArrowLeft size={14} weight="bold" /> Back to Dashboard
            </Link>

            <div className="border-b border-zinc-800 pb-6 mb-10">
                <div className="overline mb-3">// {isEdit ? "Edit" : "Create"}</div>
                <h1 className="font-display text-white text-5xl md:text-6xl leading-[0.9]">
                    {isEdit ? "EDIT POST" : "NEW POST"}<span className="text-[#FFD000]">.</span>
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                {!driveConnected && <DriveWarning />}

                <Field label="Title">
                    <input data-testid="editor-title" required value={form.title}
                        onChange={(e) => handleChange("title", e.target.value)} className="tz-input" placeholder="Post title..." />
                </Field>

                <Field label="Byline (Author)">
                    <input
                        data-testid="editor-byline"
                        value={form.author_name}
                        onChange={(e) => handleChange("author_name", e.target.value)}
                        className="tz-input"
                        placeholder="e.g. Ishaan Singh — or leave blank to use the default admin byline"
                        maxLength={120}
                    />
                    <p className="text-xs text-zinc-500 mt-2 font-mono uppercase tracking-[0.15em]">
                        Appears under the headline on the article page. Leave empty to fall back to the signed-in admin&apos;s name.
                    </p>
                </Field>

                <div className="grid md:grid-cols-2 gap-6">
                    <Field label="Category">
                        <select data-testid="editor-category" value={form.category}
                            onChange={(e) => handleChange("category", e.target.value)} className="tz-input">
                            <option value="news">News</option>
                            <option value="reviews">Reviews</option>
                            <option value="videos">Videos</option>
                            <option value="features">Features</option>
                        </select>
                    </Field>
                    <Field label="Status">
                        <label className="flex items-center gap-3 mt-3 cursor-pointer">
                            <input type="checkbox" checked={form.published}
                                onChange={(e) => handleChange("published", e.target.checked)}
                                className="w-5 h-5 accent-[#FFD000]" data-testid="editor-published" />
                            <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-300">
                                {form.published ? "Published (visible)" : "Draft (hidden)"}
                            </span>
                        </label>
                    </Field>
                </div>

                <TagsField value={form.tags} onChange={(t) => handleChange("tags", t)} />

                <CoverImageField value={form.cover_image} onChange={(url) => handleChange("cover_image", url)} />

                {form.category === "videos" && (
                    <VideoField value={form.video_url} onChange={(url) => handleChange("video_url", url)} />
                )}

                <Field label="Excerpt">
                    <textarea data-testid="editor-excerpt" required value={form.excerpt} rows={3}
                        onChange={(e) => handleChange("excerpt", e.target.value)} className="tz-input"
                        placeholder="Short summary..." />
                </Field>

                <ContentField value={form.content} onChange={(v) => handleChange("content", v)} />

                <div className="flex gap-3 pt-4 border-t border-zinc-800">
                    <button type="submit" disabled={saving} data-testid="editor-save"
                        className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
                        <FloppyDisk size={16} weight="bold" /> {saving ? "Saving..." : (isEdit ? "Update Post" : "Publish Post")}
                    </button>
                    <Link to="/admin" className="btn-secondary">Cancel</Link>
                </div>
            </form>
        </div>
    );
}
